import { faArrowRotateRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Chip,
  type ChipOwnProps,
  ListItemText,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';

import {
  RESPONSE_ACTION_STATUS,
  RESPONSE_ACTION_STATUS_COLORS,
  RESPONSE_ACTION_STATUS_LABELS,
  RESPONSE_ACTION_STATUS_REASON_LABELS,
  RESPONSE_ACTION_TARGETS,
  RESPONSE_ACTION_TYPES
} from './TicketResponseActions.constants';
import type {
  ResponseActionCellParams,
  ResponseActionDTO,
  ResponseActionStatus,
  ResponseActionTriggerCellProps
} from './TicketResponseActions.types';

const getResponseActionDetail = (data?: ResponseActionDTO) => {
  return data?.payload?.detail ?? '';
};

export const renderResponseActionTriggerCell = (
  props: ResponseActionTriggerCellProps
) => {
  const { data, disabled, hidden = false, handleRunActionClick } = props;
  const status = data?.current_status;

  if (status === RESPONSE_ACTION_STATUS.Completed || hidden) {
    return null;
  }

  const tooltipText = disabled ? 'Action is disabled' : 'Run Action';

  return (
    <Tooltip title={tooltipText}>
      <Button
        aria-label="Run response action"
        disabled={disabled}
        onClick={handleRunActionClick}
        variant="outlined"
        endIcon={<FontAwesomeIcon icon={faArrowRotateRight} />}
      >
        Run
      </Button>
    </Tooltip>
  );
};

export const renderStatusCell = (params: ResponseActionCellParams) => {
  const value = params.data?.current_status;
  const statusReason = params.data?.status_reason;

  const chipColor = value ? RESPONSE_ACTION_STATUS_COLORS[value] : 'default';
  const detail = getResponseActionDetail(params.data);
  const isSkipped = value === RESPONSE_ACTION_STATUS.Skipped;
  const chipLabel = isSkipped
    ? statusReason
      ? RESPONSE_ACTION_STATUS_REASON_LABELS[statusReason]
      : 'Not Run'
    : value
      ? RESPONSE_ACTION_STATUS_LABELS[
          value as Exclude<ResponseActionStatus, 'skipped'>
        ]
      : 'Unknown';

  return (
    <Tooltip title={detail}>
      <Chip
        label={chipLabel}
        color={chipColor as ChipOwnProps['color']}
        size="small"
      />
    </Tooltip>
  );
};

export const renderInfoCell = (params: ResponseActionCellParams) => {
  const actionType = params.data
    ?.action_type as keyof typeof RESPONSE_ACTION_TYPES;
  const label =
    RESPONSE_ACTION_TYPES[actionType] || actionType || 'Unknown Action';
  const targetKey =
    (Object.hasOwn(RESPONSE_ACTION_TARGETS, actionType)
      ? RESPONSE_ACTION_TARGETS[
          actionType as keyof typeof RESPONSE_ACTION_TARGETS
        ]
      : undefined) ?? 'target_upn';
  const targetValue = params.data?.payload?.[targetKey];
  const targetUpn = typeof targetValue === 'string' ? targetValue : undefined;

  const user = params.data?.user?.name || 'Unknown';

  return (
    <ListItemText
      sx={{ minWidth: 0, overflow: 'hidden' }}
      primary={<Typography noWrap>{label}</Typography>}
      secondary={
        <Stack sx={{ minWidth: 0 }}>
          <Typography noWrap variant="caption">
            {`Target: ${targetUpn || 'N/A'}`}
          </Typography>
          <Typography noWrap variant="caption">
            {`User: ${user || 'Unknown'}`}
          </Typography>
        </Stack>
      }
    />
  );
};
